import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_gui = api_configuration.libraries.gui_definition_lib.types.gui_model.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import parserNumber = require("../front 2 state/parseNumber");
import parseNumber = parserNumber.parse;
import * as papa from "papaparse";
import { saveAs } from "../widgets/instructions/saveAs";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import * as update from "../widgets/instructions/update";
import { CsvColumnPathBuilder } from "../navigation/CsvColumnPathBuilder";
import DestructorTree from "../DestructorTree";
import { createCSVBlob } from "../utils";
import * as Sets from "../Sets";
import { TranslationKey } from "../front/translate";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { getStaticDecimalsFromNumericalType } from "../state 2 interface/serializeNumber";
import {
	registerIdPath,
	registerText,
	registerNumber,
	registerGroup,
	registerStategroup,
	registerEntry,
} from "../state/registerClientState";
import * as GC from "../state/GarbageCollector";
import { addCollectionAttribute } from "../state/addClientState";
import { cancelDefault } from "./defaults";
import { serializeNumber } from "../state 2 interface/serializeNumber";
import { entryExists } from "../state 2 interface/entryExists";
import * as save from "../widgets/instructions/save";
import clientWorkIsDone from "./tasks";
import { ignoreCollectionDefaults } from "../state/ClientState";
import { createMutation } from "../state 2 interface/createMutation";
import { InstructionError } from "../widgets/instructions/InstructionError";
import { iText } from "../state/Text";
import { iState } from "../state/Stategroup";
import { iNode } from "../state/Node";
import { iCollection } from "../state/Collection";

// Note that there is a zero width space before the error_prefix. This is to
// prevent naming collissions with model properties starting with the same
// name. If the model contains this exact prefix. More work has to be done to
// avoid naming collissions.
const error_prefix = "☹ Error: ";

export class ImportError extends Error {
	constructor(public readonly message: TranslationKey) {
		super(message);
	}
}

type CsvImportRecord = {
	[key: string]: string;
	item_operation: "add" | "update" | "remove";
};

class ErrorReporter {
	error_fields = new Set<string>();
	create_record_reporter(record: CsvImportRecord) {
		return (column: string, error: string) => {
			const error_column = error_prefix + column;
			this.error_fields.add(error_column);
			this.error_fields.add(error_prefix);
			record[error_prefix] = "x";
			if (record[error_column] !== undefined) {
				record[error_column] = record[error_column] + ", " + error;
			} else {
				record[error_column] = error;
			}
		};
	}
}

function validateRecord(
	gui_node: api_gui.Cgui_node,
	csv_rec: CsvImportRecord,
	column_path_builder: CsvColumnPathBuilder,
	columns: Set<string>,
	reportError: (column: string, error: string) => void
) {
	for (const [attribute_name, attribute] of gui_node.properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "reference set":
				break;
			case "action":
				break;
			case "command":
				break;
			case "property": {
				const property = attribute.properties.type.state.node;
				const kind = property.inferences.property_type().properties.type.state.node.output.value_source();
				switch (kind.variant.name) {
					case "derived":
						break;
					case "base":
						switch (property.properties.type.state.name) {
							case "text":
								break;
							case "number": {
								const number = property.properties.type.state.node;
								const base_number = number.inferences.number().properties.type.cast("base");
								switch (base_number.properties.type.state.name) {
									case "causal":
										break;
									case "simple": {
										const number_column = column_path_builder.number(attribute_name).build();
										const value = csv_rec[number_column];
										if (value) {
											// An empty string is valid: the number will not be set
											const number_type = number.inferences.number().properties.set_type.state.name;
											const decimals = getStaticDecimalsFromNumericalType(
												number.inferences.number().properties.numerical_type.ref
											);
											const number_parse_result = parseNumber(
												value,
												number.properties.numerical_type.ref.key.ref,
												number_type,
												decimals
											);
											switch (number_parse_result[0]) {
												case "no":
													reportError(
														number_column,
														"Invalid '" + number.properties.numerical_type.entry + "': " + number_parse_result[1].error
													);
													break;
												case "yes":
													break;
												default:
													unexpectedState(number_parse_result[0]);
											}
										}
										break;
									}
									default:
										unexpectedState(base_number.properties.type.state);
								}
								break;
							}
							case "file":
								break;
							case "group": {
								const group = property.properties.type.state.node;
								validateRecord(
									group.properties.gui_node,
									csv_rec,
									column_path_builder.group(attribute_name),
									columns,
									reportError
								);
								break;
							}
							case "state group": {
								const stategroup = property.properties.type.state.node;
								const stategroup_column = column_path_builder.state_group(attribute_name).build();
								const value = csv_rec[stategroup_column];
								if (value) {
									// An empty string is valid: the stategroup will not be set
									const selected_state = stategroup.properties.states.get(value);
									if (!selected_state) {
										reportError(
											stategroup_column,
											"Must be one of: " + Array.from(stategroup.properties.states).map((state) => state[0])
										);
									}
								}
								for (const [state_name, state] of stategroup.properties.states) {
									validateRecord(
										state.properties.gui_node,
										csv_rec,
										column_path_builder.state(attribute_name, state_name),
										columns,
										reportError
									);
								}
								break;
							}
							case "collection":
								break;
							default:
								unexpectedState(property.properties.type.state);
						}
						break;
					default:
						unexpectedState(kind.variant);
				}
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state);
		}
	}
}

function findUsedColumns(
	gui_node: api_gui.Cgui_node,
	column_path_builder: CsvColumnPathBuilder,
	columns: Set<string>
): Set<string> {
	let used_columns = new Set<string>();
	for (const [attribute_name, attribute] of gui_node.properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "reference set":
				break;
			case "action":
				break;
			case "command":
				break;
			case "property": {
				const property = attribute.properties.type.state.node;
				const kind = property.inferences.property_type().properties.type.state.node.output.value_source();
				switch (kind.variant.name) {
					case "derived":
						break;
					case "base":
						switch (property.properties.type.state.name) {
							case "text": {
								const text_column = column_path_builder.text(attribute_name).build();
								if (columns.has(text_column)) {
									used_columns.add(text_column);
								}
								break;
							}
							case "number": {
								const number = property.properties.type.state.node;
								const base_number = number.inferences.number().properties.type.cast("base");
								switch (base_number.properties.type.state.name) {
									case "causal":
										break;
									case "simple": {
										const number_column = column_path_builder.number(attribute_name).build();
										if (columns.has(number_column)) {
											used_columns.add(number_column);
										}
										break;
									}
									default:
										unexpectedState(base_number.properties.type.state);
								}
								break;
							}
							case "file":
								break;
							case "group": {
								const group = property.properties.type.state.node;
								used_columns = Sets.union(
									used_columns,
									findUsedColumns(group.properties.gui_node, column_path_builder.group(attribute_name), columns)
								);
								break;
							}
							case "state group": {
								const stategroup = property.properties.type.state.node;
								const stategroup_column = column_path_builder.state_group(attribute_name).build();
								if (columns.has(stategroup_column)) {
									used_columns.add(stategroup_column);
								}
								// Don't throw a fitty, ignore this for now even though we don't use it
								const stategroup_operation_column = column_path_builder.state_group_operation(attribute_name).build();
								if (columns.has(stategroup_operation_column)) {
									used_columns.add(stategroup_operation_column);
								}
								for (const [state_name, state] of stategroup.properties.states) {
									used_columns = Sets.union(
										used_columns,
										findUsedColumns(
											state.properties.gui_node,
											column_path_builder.state(attribute_name, state_name),
											columns
										)
									);
								}
								break;
							}
							case "collection":
								break;
							default:
								unexpectedState(property.properties.type.state);
						}
						break;
					default:
						unexpectedState(kind.variant);
				}
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state);
		}
	}
	return used_columns;
}

function mergeErrorFields(fields: Set<string>, error_fields: Set<string>) {
	const result = new Set<string>();
	for (const field of fields) {
		result.add(field);
		const error_field_name = error_prefix + field;
		if (error_fields.has(error_field_name)) {
			result.add(error_field_name);
			error_fields.delete(error_field_name);
		}
	}
	for (const field of error_fields) {
		result.add(field);
	}
	return result;
}

async function validateCsv(
	context_node: api_cs.Cnode,
	gui_node: api_gui.Cgui_node,
	key_fields: Set<string>,
	fields: Set<string>,
	last_key_field: string,
	records: CsvImportRecord[]
) {
	const error_reporter = new ErrorReporter();
	// Validate key_fields
	key_fields.add("item_operation"); // add to the key fields so it is noted when missing and not marked as unknown later
	const missing_key_fields = Sets.difference(key_fields, fields);
	if (missing_key_fields.size > 0) {
		// We'll report the errors on the first row.
		// Since the column is missing there is no chance of clashes
		if (!records[0]) {
			records.push({ item_operation: "add" });
		}
		const reportError = error_reporter.create_record_reporter(records[0]);
		for (const col of missing_key_fields) {
			reportError(col, "Key column is missing");
		}
	}
	// Validate fields
	const columns = Sets.difference(fields, key_fields);
	const used_columns = findUsedColumns(gui_node, CsvColumnPathBuilder.create(), columns);
	const unused_columns = Sets.difference(columns, used_columns);
	if (unused_columns.size > 0) {
		// We'll report the errors on the first row.
		// Since the column is not valid there is no chance of clashes
		if (!records[0]) {
			records.push({ item_operation: "add" });
		}
		const reportError = error_reporter.create_record_reporter(records[0]);
		for (const col of unused_columns) {
			reportError(col, "Column unknown");
		}
	}
	// Validate data
	const nodes_to_check_for_existence = new Map<string, { id_path: IdPathBuilder; records: Set<CsvImportRecord> }>();
	for (const csv_record of records) {
		const reportError = error_reporter.create_record_reporter(csv_record);
		const [id_path_builder] = getRecordIdPath(context_node, gui_node, csv_record);
		switch (csv_record.item_operation) {
			case "add": {
				// The path always end in a collection entry. So removing the
				// tail will get us the parent node.
				const parent_id_path_builder = id_path_builder.removeTail();
				const id = IdPathBuilder.serialize(parent_id_path_builder);
				const to_check = nodes_to_check_for_existence.get(id);
				if (to_check === undefined) {
					nodes_to_check_for_existence.set(id, {
						id_path: parent_id_path_builder,
						records: new Set([csv_record]),
					});
				} else {
					to_check.records.add(csv_record);
				}
				break;
			}
			case "update": {
				nodes_to_check_for_existence.set(IdPathBuilder.serialize(id_path_builder), {
					id_path: id_path_builder,
					records: new Set([csv_record]),
				});
				break;
			}
			case "remove": {
				nodes_to_check_for_existence.set(IdPathBuilder.serialize(id_path_builder), {
					id_path: id_path_builder,
					records: new Set([csv_record]),
				});
				break;
			}
			default:
				reportError("item_operation", "Must be one of: add, update, remove");
		}
		validateRecord(gui_node, csv_record, CsvColumnPathBuilder.create(), columns, reportError);
	}

	await Promise.all(
		[...nodes_to_check_for_existence.values()].map(async ({ id_path, records }) => {
			if (!(await entryExists(id_path))) {
				for (const record of records) {
					error_reporter.create_record_reporter(record)(last_key_field, "Entry does not exist.");
				}
			}
		})
	);

	if (error_reporter.error_fields.size > 0) {
		const field_for_errors_csv = [...mergeErrorFields(fields, error_reporter.error_fields)];
		saveAs(createCSVBlob(papa.unparse({ data: records, fields: field_for_errors_csv })), "errors.csv");
		throw new ImportError("The CSV file is not valid. Refer to the error report and try again.");
	}
}

function getRecordIdPath(
	context_node: api_cs.Cnode,
	target_node_def: api_gui.Cgui_node,
	record: CsvImportRecord
): [IdPathBuilder, string] {
	if (target_node_def.input.application_node().is(context_node.input.node_type())) {
		return [createIdPathBuilderFromNode(context_node), ""];
	}
	const location = target_node_def.output.location();
	switch (location.variant.name) {
		case "root":
			return [new IdPathBuilder(), ""];
		case "collection": {
			const $ = location.variant.definition;
			const [id_path_builder, prefix] = getRecordIdPath(context_node, $.parent.parent.parent, record);
			const collection_name = $.parent.parent.key.entry;
			const field_name = prefix + collection_name;
			const key = record[field_name];
			return [id_path_builder.collectionEntry(collection_name, key), field_name + "."];
		}
		case "state": {
			const $ = location.variant.definition;
			const [id_path_builder, prefix] = getRecordIdPath(context_node, $.parent.parent.parent.parent, record);
			const stategroup_name = $.parent.parent.parent.key.entry;
			const state_name = $.key.entry;
			return [id_path_builder.state(stategroup_name, state_name), prefix];
		}
		case "group": {
			const $ = location.variant.definition;
			const [id_path_builder, prefix] = getRecordIdPath(context_node, $.parent.parent.parent, record);
			const group_name = $.parent.parent.key.entry;
			return [id_path_builder.group(group_name), prefix];
		}
		case "operation":
			throw new Error("Operations not supported in CSV importer");
		default:
			unexpectedState(location.variant);
	}
}

function registerNodeFromRecord(
	node: api_cs.Cnode,
	record: CsvImportRecord,
	destructor: DestructorTree,
	column_path_builder: CsvColumnPathBuilder
) {
	const node_type = node.input.node_type();
	for (const [attribute_name, attribute] of node_type.properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "reference set":
				break;
			case "action":
				break;
			case "command":
				break;
			case "property": {
				const property_def = attribute.properties.type.state.node;
				const kind = property_def.properties.type.state.node.output.value_source();
				switch (kind.variant.name) {
					case "derived":
						break;
					case "base":
						switch (property_def.properties.type.state.name) {
							case "text": {
								const text_def = property_def.properties.type.state.node;
								const text_column = column_path_builder.text(attribute_name).build();
								const value = record[text_column];
								if (value) {
									// If empty or undefined, do not register
									const text = registerText(text_def, node, destructor);
									cancelDefault(text.parent, destructor);
								}
								break;
							}
							case "number": {
								const number_def = property_def.properties.type.state.node;
								const base_number = number_def.properties.type.cast("base");
								switch (base_number.properties.type.state.name) {
									case "causal":
										break;
									case "simple": {
										const number_column = column_path_builder.number(attribute_name).build();
										const value = record[number_column];
										if (value) {
											// If empty or undefined, do not register
											const number = registerNumber(number_def, node, destructor);
											cancelDefault(number.parent, destructor);
										}
										break;
									}
									default:
										unexpectedState(base_number.properties.type.state);
								}
								break;
							}
							case "file":
								break;
							case "group": {
								const group_def = property_def.properties.type.state.node;
								const group = registerGroup(group_def, node, destructor);
								registerNodeFromRecord(
									group.properties.node,
									record,
									destructor,
									column_path_builder.group(attribute_name)
								);
								break;
							}
							case "state group": {
								const stategroup_def = property_def.properties.type.state.node;
								const stategroup = registerStategroup(stategroup_def, node, destructor);
								const stategroup_column = column_path_builder.state_group(attribute_name).build();
								const value = record[stategroup_column];
								if (value) {
									cancelDefault(stategroup.parent, destructor);
								}
								for (const [state_name, state] of stategroup.properties.states) {
									registerNodeFromRecord(
										state.properties.node,
										record,
										destructor,
										column_path_builder.state(attribute_name, state_name)
									);
								}
								break;
							}
							case "collection":
								break;
							default:
								unexpectedState(property_def.properties.type.state);
						}
						break;
					default:
						unexpectedState(kind.variant);
				}
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state);
		}
	}
}

function updateNodeFromRecord(node: api_cs.Cnode, record: CsvImportRecord, column_path_builder: CsvColumnPathBuilder) {
	for (const [attribute_name, attribute] of node.properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "operation":
				break;
			case "property": {
				const property = attribute.properties.type.state.node;
				switch (property.properties.type.state.name) {
					case "text": {
						const text = property.properties.type.state.node;
						const text_column = column_path_builder.text(attribute_name).build();
						const value = record[text_column];
						if (value) {
							// If empty or undefined, do not set
							iText(text).__update(value, "user");
						}
						break;
					}
					case "number": {
						const number = property.properties.type.state.node;
						const number_column = column_path_builder.number(attribute_name).build();
						const value = record[number_column];
						if (value) {
							// If empty or undefined, do not set
							const number_type = number.inferences.number_type();
							const number_parse_result = parseNumber(
								value,
								number_type.properties.numerical_type.ref,
								number_type.properties.set_type.state.name,
								getStaticDecimalsFromNumericalType(number_type.properties.numerical_type.ref)
							);
							switch (number_parse_result[0]) {
								case "no":
									number.properties.is_valid.set("no", {
										"exceeds maximum": "no",
										"exceeds minimum": "no",
										"not a number": "yes",
										"not set": "no",
										"should be positive": "no",
									});
									break;
								case "yes": {
									const number_value = (number_parse_result[1] as any).number;
									update.number(number, number_value, "user");
									break;
								}
							}
						}
						break;
					}
					case "file":
						break;
					case "group":
						updateNodeFromRecord(
							property.properties.type.state.node.properties.node,
							record,
							column_path_builder.group(attribute_name)
						);
						break;
					case "state group": {
						const stategroup = property.properties.type.state.node;
						const stategroup_column = column_path_builder.state_group(attribute_name).build();
						const value = record[stategroup_column];
						if (value) {
							// If empty or undefined, do not set
							const state_to_set = stategroup.properties.states.get(value)!;
							iState(state_to_set).__select("user");
						}
						for (const [state_name, state] of stategroup.properties.states) {
							updateNodeFromRecord(
								state.properties.node,
								record,
								column_path_builder.state(attribute_name, state_name)
							);
						}
						break;
					}
					case "collection":
						break;
				}
				break;
			}
		}
	}
}

async function executeCsv(
	context_node: api_cs.Cnode,
	target_node_def: api_gui.Cgui_node,
	records: CsvImportRecord[],
	error_reporter: ErrorReporter,
	last_key_field: string
) {
	ignoreCollectionDefaults.set(true);
	const destructor = new DestructorTree();
	const record_entry_map = new Map<CsvImportRecord, api_cs.Centries__collection>();
	for (const record of records) {
		const [id_path_builder, prefix] = getRecordIdPath(context_node, target_node_def, record);
		const key_field = prefix.slice(0, -1); // The prefix has a "." at the end: remove
		let entry: api_cs.Centries__collection;
		switch (record.item_operation) {
			case "add": {
				// The path always end in a collection entry. So removing the
				// tail will get us the parent node.
				const parent_id_path_builder = id_path_builder.removeTail();
				const parent_node = registerIdPath(parent_id_path_builder, destructor);
				const collection_def = target_node_def.input.location().cast("collection").inferences.collection();
				const key = record[key_field];
				const collection = addCollectionAttribute(collection_def, parent_node);
				entry = iCollection(collection).addEntry("user", key);
				registerEntry(entry, destructor);
				registerNodeFromRecord(entry.properties.node, record, destructor, CsvColumnPathBuilder.create());
				break;
			}
			case "update": {
				const node = registerIdPath(id_path_builder, destructor);
				entry = node.input.entity().cast("entry");
				registerNodeFromRecord(node, record, destructor, CsvColumnPathBuilder.create());
				break;
			}
			case "remove": {
				const node = registerIdPath(id_path_builder, destructor);
				entry = node.input.entity().cast("entry");
				break;
			}
		}
		record_entry_map.set(record, entry);
	}

	await clientWorkIsDone();
	for (const [record, entry] of record_entry_map) {
		switch (record.item_operation) {
			case "add":
				updateNodeFromRecord(entry.properties.node, record, CsvColumnPathBuilder.create());
				break;
			case "update":
				updateNodeFromRecord(entry.properties.node, record, CsvColumnPathBuilder.create());
				break;
			case "remove":
				entry.properties.location.cast("remote").properties.marked_for_deletion.set("yes", {});
				break;
		}
	}

	await clientWorkIsDone();
	for (const [record, entry] of record_entry_map) {
		switch (record.item_operation) {
			case "add":
				reportErrorsOnNode(
					entry.properties.node,
					record,
					CsvColumnPathBuilder.create(),
					error_reporter.create_record_reporter(record)
				);
				break;
			case "update":
				reportErrorsOnNode(
					entry.properties.node,
					record,
					CsvColumnPathBuilder.create(),
					error_reporter.create_record_reporter(record)
				);
				break;
			case "remove":
				break;
		}
	}

	const valid_entries = [...record_entry_map].filter(([record, entry]) => {
		const node = entry.properties.node;
		switch (record.item_operation) {
			case "add":
				switch (node.properties.is_valid.state.name) {
					case "no":
						return false;
					case "yes":
						return true;
				}
				break;
			case "update":
				switch (node.properties.is_valid.state.name) {
					case "no":
						return false;
					case "yes":
						return true;
				}
				break;
			case "remove":
				return true;
		}
	});
	try {
		await save.sendMutation(context_node, createMutation(new Set(valid_entries.map(([, entry]) => entry))));
	} catch (e) {
		if (e instanceof InstructionError) {
			for (const [record] of valid_entries) {
				error_reporter.create_record_reporter(record)(last_key_field, "Failed to save the entry.");
			}
		} else {
			throw e;
		}
	}
	iNode(context_node).discard();
	destructor.destroy();
	GC.cleanNode(context_node);
	ignoreCollectionDefaults.set(false);
}

async function importCsvFile(file: File): Promise<papa.ParseResult> {
	const csv_data = await new Promise<papa.ParseResult>((resolve) => {
		papa.parse(file, {
			header: true,
			complete: (csv_data) => resolve(csv_data),
			skipEmptyLines: true,
		});
	});
	if (csv_data.errors.length > 0) {
		saveAs(createCSVBlob(papa.unparse(csv_data.errors)), "errors.csv");
		throw new ImportError("The CSV file is not valid. Refer to the error report and try again.");
	}
	return csv_data;
}

function getKeyFieldNames(
	context_node: api_app.Cnode__application,
	target_node: api_gui.Cgui_node
): [Set<string>, string] {
	if (target_node.input.application_node().is(context_node)) {
		return [new Set<string>(), ""];
	}
	const location = target_node.output.location();
	switch (location.variant.name) {
		case "root":
			return [new Set<string>(), ""];
		case "collection": {
			const $ = location.variant.definition;
			const [key_fields, prefix] = getKeyFieldNames(context_node, $.parent.parent.parent);
			const field_name = prefix + $.parent.parent.key.entry;
			key_fields.add(field_name);
			return [key_fields, field_name + "."];
		}
		case "state":
			return getKeyFieldNames(context_node, location.variant.definition.parent.parent.parent.parent);
		case "group":
			return getKeyFieldNames(context_node, location.variant.definition.parent.parent.parent);
		case "operation":
			switch (location.variant.definition.variant.name) {
				case "action": {
					const $ = location.variant.definition.variant.definition;
					return getKeyFieldNames(context_node, $.parent.parent);
				}
				case "command": {
					const $ = location.variant.definition.variant.definition;
					return getKeyFieldNames(context_node, $.parent.parent);
				}
				default:
					return unexpectedState(location.variant.definition.variant);
			}
		default:
			unexpectedState(location.variant);
	}
}

function reportErrorsOnNode(
	node: api_cs.Cnode,
	csv_record: CsvImportRecord,
	column_path_builder: CsvColumnPathBuilder,
	report_error: (column_name: string, error: string) => void
) {
	switch (node.properties.is_valid.state.name) {
		case "no": {
			for (const [attribute_name, attribute] of node.properties.attributes) {
				switch (attribute.properties.type.state.name) {
					case "operation":
						break;
					case "property": {
						const property = attribute.properties.type.state.node;
						switch (property.properties.type.state.name) {
							case "collection":
								break;
							case "file":
								break;
							case "group": {
								const $ = property.properties.type.state.node;
								reportErrorsOnNode(
									$.properties.node,
									csv_record,
									column_path_builder.group(attribute_name),
									report_error
								);
								break;
							}
							case "number": {
								const number_property = property.properties.type.state.node;
								switch (number_property.properties.is_valid.state.name) {
									case "no": {
										const not_valid = number_property.properties.is_valid.state.node;
										const column_name = column_path_builder.number(attribute_name).build();
										let message = "Not valid";
										switch (not_valid.properties.should_be_positive.state.name) {
											case "no":
												break;
											case "yes": {
												message += " must be positive";
												break;
											}
											default:
												unexpectedState(not_valid.properties.should_be_positive.state);
										}
										switch (not_valid.properties.exceeds_minimum.state.name) {
											case "no":
												break;
											case "yes": {
												const $ = not_valid.properties.exceeds_minimum.state.node;
												const numerical_type = number_property.inferences.number_type().properties.numerical_type.ref;
												message +=
													" less then minimum: " +
													serializeNumber(
														$.properties.minimum.value,
														getStaticDecimalsFromNumericalType(numerical_type),
														numerical_type,
														"english"
													);
												break;
											}
											default:
												unexpectedState(not_valid.properties.exceeds_minimum.state);
										}
										switch (not_valid.properties.exceeds_maximum.state.name) {
											case "no":
												break;
											case "yes": {
												const $ = not_valid.properties.exceeds_maximum.state.node;
												const numerical_type = number_property.inferences.number_type().properties.numerical_type.ref;
												message +=
													" greater than maximum: " +
													serializeNumber(
														$.properties.maximum.value,
														getStaticDecimalsFromNumericalType(numerical_type),
														numerical_type,
														"english"
													);
												break;
											}
											default:
												unexpectedState(not_valid.properties.exceeds_maximum.state);
										}
										report_error(column_name, message);
										break;
									}
									case "yes":
										break;
									default:
										unexpectedState(number_property.properties.is_valid.state);
								}

								break;
							}
							case "state group": {
								const state_group_property = property.properties.type.state.node;
								switch (state_group_property.properties.is_valid.state.name) {
									case "no": {
										let message = "";
										const not_valid = state_group_property.properties.is_valid.state.node;
										switch (not_valid.properties.no_state_selected.state.name) {
											case "no":
												break;
											case "yes": {
												message = "not set";
												break;
											}
											default:
												unexpectedState(not_valid.properties.no_state_selected.state);
										}

										switch (state_group_property.properties.is_set.state.name) {
											case "no":
												break;
											case "yes": {
												const $ = state_group_property.properties.is_set.state.node;
												switch (not_valid.properties.invalid_rules.state.name) {
													case "no":
														break;
													case "yes": {
														const rules = $.properties.state.ref.properties.rules.properties.rules;
														for (const rule of rules) {
															switch (rule[1].properties.is_valid.state.name) {
																case "no": {
																	message += " violates rule " + rule[0];
																	break;
																}
																case "yes":
																	break;
																default:
																	unexpectedState(rule[1].properties.is_valid.state);
															}
														}

														break;
													}
													default:
														unexpectedState(not_valid.properties.invalid_rules.state);
												}
												reportErrorsOnNode(
													$.properties.state.ref.properties.node,
													csv_record,
													column_path_builder.state(attribute_name, $.properties.state.entry),
													report_error
												);
												break;
											}
											default:
												unexpectedState(state_group_property.properties.is_set.state);
										}
										if (message.length > 0) {
											const column_name = column_path_builder.state_group(attribute_name).build();
											report_error(column_name, message);
										}
										break;
									}
									case "yes":
										break;
									default:
										unexpectedState(state_group_property.properties.is_valid.state);
								}

								break;
							}
							case "text": {
								const text_property = property.properties.type.state.node;
								switch (text_property.properties.is_valid.state.name) {
									case "no": {
										let message = "";
										const column_name = column_path_builder.text(attribute_name).build();
										const not_valid = text_property.properties.is_valid.state.node;
										switch (not_valid.properties.invalid_rules.state.name) {
											case "no":
												break;
											case "yes": {
												const rules = text_property.properties.navigable.cast("yes").properties.rules.properties.rules;
												for (const rule of rules) {
													switch (rule[1].properties.is_valid.state.name) {
														case "no": {
															message += "violate rule(s):" + rule[0];
															break;
														}
														case "yes":
															break;
														default:
															unexpectedState(rule[1].properties.is_valid.state);
													}
												}
												//cast to reference and walk rules
												break;
											}
											default:
												unexpectedState(not_valid.properties.invalid_rules.state);
										}
										switch (not_valid.properties.key_is_not_unique.state.name) {
											case "no":
												break;
											case "yes": {
												message += " key must be unique";
												break;
											}
											default:
												unexpectedState(not_valid.properties.key_is_not_unique.state);
										}
										switch (not_valid.properties.text_does_not_match_pattern.state.name) {
											case "no":
												break;
											case "yes": {
												const $ = not_valid.properties.text_does_not_match_pattern.state.node;
												message += " text must match pattern " + $.properties.pattern;
												break;
											}
											default:
												unexpectedState(not_valid.properties.text_does_not_match_pattern.state);
										}
										switch (not_valid.properties.reference_not_resolved.state.name) {
											case "no":
												break;
											case "yes": {
												message += " must reference a valid entry";
												break;
											}
											default:
												unexpectedState(not_valid.properties.reference_not_resolved.state);
										}
										report_error(column_name, message);
										break;
									}
									case "yes":
										break;
									default:
										unexpectedState(text_property.properties.is_valid.state);
								}
								break;
							}
							default:
								unexpectedState(property.properties.type.state);
						}
						break;
					}
					default:
						unexpectedState(attribute.properties.type.state);
				}
			}

			break;
		}
		case "yes":
			break;
		default:
			unexpectedState(node.properties.is_valid.state);
	}
}

const BATCH_SIZE = 250;
export async function importCsv(
	context_node: api_cs.Cnode,
	target_node_def: api_gui.Cgui_node,
	file: File
): Promise<void> {
	const csv_data = await importCsvFile(file);
	const records = csv_data.data;
	const fields = new Set(csv_data.meta.fields);
	// Remove any error columns in case the user edited the errors file and reuploaded
	const error_fields = csv_data.meta.fields.filter((f) => f.startsWith(error_prefix));
	for (const error_field of error_fields) {
		for (const record of records) {
			delete record[error_field];
		}
		fields.delete(error_field);
	}
	// Validate the executability of the CSV
	const [key_fields] = getKeyFieldNames(context_node.input.node_type(), target_node_def);
	const last_key_field = [...key_fields][key_fields.size - 1] ?? "";
	await validateCsv(context_node, target_node_def, key_fields, fields, last_key_field, records);
	const number_of_csv_entries = csv_data.data.length;
	let error_batch: typeof records = [];
	const error_fields_during_run = new Set<string>();
	for (let i = 0; i < number_of_csv_entries; i += BATCH_SIZE) {
		const csv_data_batch = records.slice(i, i + BATCH_SIZE);
		const error_reporter = new ErrorReporter();
		await executeCsv(context_node, target_node_def, csv_data_batch, error_reporter, last_key_field);
		if (error_reporter.error_fields.size > 0) {
			for (const field of error_reporter.error_fields) {
				error_fields_during_run.add(field);
			}
			error_batch = error_batch.concat(csv_data_batch);
		}
	}
	if (error_fields_during_run.size > 0) {
		const field_for_errors_csv = [...mergeErrorFields(fields, error_fields_during_run)];
		saveAs(createCSVBlob(papa.unparse({ data: error_batch, fields: field_for_errors_csv })), "errors.csv");
		throw new ImportError("The CSV file is not valid. Refer to the error report and try again.");
	}
}
